import { TourLifecycleManager } from "@/lib/tour/tourLifecycleManager";
import { omit, sortBy } from "lodash-es";
import { autorun, makeAutoObservable, reaction, runInAction, toJS } from "mobx";
import * as models from "@common/models";
import { replaceAll, toTitleCase } from "@/lib/utils";
import { reloadWindow } from "@/lib/window";
import { PyneClient } from "@/PyneClient";
import { globalEventBus } from "@/lib/events/eventBus";
import { DebuggerStateStorage } from "@/lib/tour/persistent-storage/debuggerState";
import { PyneSettings } from "@common/schemas";
import { FlowKey } from "@/lib/tour/tourPlayback/flow";
import { GuideRegistry } from "./GuideRegistry";
import { TargetingEvaluator } from "./TargetingEvaluator";
import { UserStateStorage } from "@/lib/tour/persistent-storage/userState";
import { ChecklistRegistry } from "./ChecklistRegistry";
import { Step } from "@/lib/tour/steps/v2/step";

export interface DebuggerManagerParams {
  tourLifecycleManager: TourLifecycleManager;
  pyneClient: PyneClient;
  storage: DebuggerStateStorage;
  userStateStorage: UserStateStorage;
  pyneSettings: PyneSettings;
  guideRegistry: GuideRegistry;
  targetingEvaluator: TargetingEvaluator;
  checklistRegistry: ChecklistRegistry;
}

export type AdvancedDetailsTab = "guide" | "targeting" | "step" | "state";

export class DebuggerManager {
  isMinimized = true;
  isTourDetailsOpenedFromGuideList = false;
  isGuideListModalVisible = false;
  isTourDetailsModalVisible = false;
  isAdvancedDetailsEnabled = false;
  isUserDataModalVisible = false;
  isReportBugModalVisible = false;
  isStateDataModalVisible = false;
  isChecklistListModalVisible = false;
  tourDetailsModalWidth = 280;
  advancedDetailsTab: AdvancedDetailsTab = "targeting";
  selectedFlowKey: FlowKey | null = null;
  tourLifecycleManager: TourLifecycleManager;
  guideRegistry: GuideRegistry;
  checklistRegistry: ChecklistRegistry;
  targetingEvaluator: TargetingEvaluator;
  pyneClient: PyneClient;
  storage: DebuggerStateStorage;
  userStateStorage: UserStateStorage;
  pyneSettings: PyneSettings;
  selectedGuideId?: string;
  selectedStepId?: string;
  lastActiveTourStep?: Step;
  isSubmitBugLoading = false;
  isSubmitBugSuccess = false;
  isSubmitBugFailed = false;
  isDevModeEnabled = false;
  isSelectorPickerActive = false;
  isAuthenticatedUser = false;
  isSelectorUpdateLoading = false;
  isSelectorUpdateSuccessToastVisible = false;
  isSelectorUpdateFailedToastVisible = false;
  selectorPickingState: {
    guideId: string;
    stepId: string;
  } | null = null;

  pendingSelectorUpdatesByGuide: Record<
    string,
    Array<{ stepId: string; selector: string; url: string; rank?: number }>
  > = {};
  editableGuides: Record<string, models.Guide | null> = {};
  lastSubmittedGuideIdForSelectorUpdates: string | null = null;
  pendingScreenshotsByStep: Record<string, { guideId: string; screenshot: string }> = {};

  bugReportId?: string;

  constructor({
    tourLifecycleManager,
    pyneClient,
    storage,
    userStateStorage,
    pyneSettings,
    guideRegistry,
    checklistRegistry,
    targetingEvaluator,
  }: DebuggerManagerParams) {
    makeAutoObservable(this);
    this.tourLifecycleManager = tourLifecycleManager;
    this.pyneClient = pyneClient;
    this.storage = storage;
    this.pyneSettings = pyneSettings;
    this.guideRegistry = guideRegistry;
    this.checklistRegistry = checklistRegistry;
    this.userStateStorage = userStateStorage;

    const {
      isMinimized,
      isAdvancedDetailsEnabled,
      isGuideListModalVisible,
      isReportBugModalVisible,
      isTourDetailsModalVisible,
      isTourDetailsOpenedFromGuideList,
      isUserDataModalVisible,
      isStateDataModalVisible,
      isDevModeEnabled,
      selectedGuideId,
      selectedStepId,
      advancedDetailsTab,
    } = this.storage.get();

    this.isMinimized = isMinimized;
    this.isAdvancedDetailsEnabled = isAdvancedDetailsEnabled;
    this.isGuideListModalVisible = isGuideListModalVisible;
    this.isReportBugModalVisible = isReportBugModalVisible;
    this.isTourDetailsModalVisible = isTourDetailsModalVisible;
    this.isTourDetailsOpenedFromGuideList = isTourDetailsOpenedFromGuideList;
    this.isUserDataModalVisible = isUserDataModalVisible;
    this.isStateDataModalVisible = isStateDataModalVisible;
    this.selectedGuideId = selectedGuideId;
    this.selectedStepId = selectedStepId;
    this.advancedDetailsTab = advancedDetailsTab as AdvancedDetailsTab;
    this.isDevModeEnabled = isDevModeEnabled;
    this.targetingEvaluator = targetingEvaluator;

    reaction(
      () => this.tourLifecycleManager.currentTour,
      (currentTour) => {
        if (currentTour) {
          this.followRunningStep();
          this.setAdvancedDetailsTab("step");
        } else {
          this.setAdvancedDetailsTab("targeting");
          this.stopFollowActiveStep();
          this.setSelectedFlowKey(null);
          this.selectedStepId = undefined;
        }
      }
    );

    reaction(
      () => this.tourPlaybackManager?.currentFlowKey,
      (flowKey) => {
        this.setSelectedFlowKey("tour");
        if (this.isAuthenticatedUser) {
          this.openTourDetails();
        }
      }
    );

    reaction(
      () =>
        [this.tourPlaybackManager?.currentStep, this.tourPlaybackManager?.currentFlowKey] as [
          Step | undefined,
          FlowKey | undefined,
        ],
      ([step, flowKey]) => {
        if (flowKey === "tour") {
          this.lastActiveTourStep = step;
        }
      }
    );

    autorun(() => {
      this.storage.set({
        isMinimized: this.isMinimized,
        isAdvancedDetailsEnabled: this.isAdvancedDetailsEnabled,
        isTourDetailsOpenedFromGuideList: this.isTourDetailsOpenedFromGuideList,
        isGuideListModalVisible: this.isGuideListModalVisible,
        isTourDetailsModalVisible: this.isTourDetailsModalVisible,
        isUserDataModalVisible: this.isUserDataModalVisible,
        isReportBugModalVisible: this.isReportBugModalVisible,
        isStateDataModalVisible: this.isStateDataModalVisible,
        selectedGuideId: this.selectedGuideId,
        selectedStepId: this.selectedStepId,
        advancedDetailsTab: this.advancedDetailsTab,
        isDevModeEnabled: this.isDevModeEnabled,
      });
    });

    globalEventBus.use("*", (context) => {
      return {
        ...context,
        pageContext: {
          page: {
            path: location.pathname,
            search: location.search,
            title: document.title,
            url: location.href,
          },
          userAgentData: (navigator as any).userAgentData,
          userAgent: navigator.userAgent,
          locale: navigator.language,
        },
      };
    });

    this.setupMessageListeners();
  }

  setupMessageListeners() {
    this.requestIsAuthenticated();

    window.addEventListener("message", (event) => {
      this.onMessage(event);
    });
  }

  onMessage(event: MessageEvent) {
    switch (event.data.type) {
      case `pyne-injected-script/alive`: {
        this.requestIsAuthenticated();
        break;
      }
      case `pyne-extension/is-authenticated`: {
        this.onIsAuthenticatedChanged(event.data);
        break;
      }
      case `pyne-extension/selector-update-completed`: {
        this.onSelectorUpdateCompleted(event.data);
        break;
      }
      case `pyne-extension/screenshot-captured-for-step`: {
        this.onScreenshotCapturedForStep(event.data);
        break;
      }
    }
  }

  requestIsAuthenticated() {
    window.postMessage(
      {
        type: `pyne-debugger/request-is-authenticated`,
      },
      "*"
    );
  }

  requestScreenshotForCurrentlyPickingStep() {
    if (!this.selectorPickingState?.stepId) return;

    window.postMessage(
      {
        type: `pyne-debugger/request-screenshot-for-step`,
        stepId: this.selectorPickingState.stepId,
      },
      "*"
    );
  }

  onIsAuthenticatedChanged(data: { isAuthenticated: boolean }) {
    this.isAuthenticatedUser = data.isAuthenticated;
  }

  onSelectorUpdateCompleted(data: { success: boolean }) {
    this.isSelectorUpdateLoading = false;
    if (data.success) {
      this.isSelectorUpdateSuccessToastVisible = true;
      if (this.lastSubmittedGuideIdForSelectorUpdates) {
        const submittedGuideId = this.lastSubmittedGuideIdForSelectorUpdates;
        runInAction(() => {
          delete this.pendingSelectorUpdatesByGuide[submittedGuideId];
          delete this.editableGuides[submittedGuideId];

          Object.keys(this.pendingScreenshotsByStep).forEach((stepId) => {
            if (this.pendingScreenshotsByStep[stepId]?.guideId === submittedGuideId) {
              delete this.pendingScreenshotsByStep[stepId];
            }
          });
        });
        this.lastSubmittedGuideIdForSelectorUpdates = null;
      }
    } else {
      this.isSelectorUpdateFailedToastVisible = true;
    }
  }

  onScreenshotCapturedForStep(data: { stepId: string; screenshot: string }) {
    console.log("XX onScreenshotCapturedForStep", data);
    if (!this.selectorPickingState?.guideId) {
      console.warn(
        `[DebuggerManager] Screenshot captured for step ${data.stepId} but no guide context is available (selectorPickingState.guideId is missing).`
      );
      return;
    }

    const guideId = this.selectorPickingState.guideId;

    runInAction(() => {
      this.pendingScreenshotsByStep[data.stepId] = { guideId, screenshot: data.screenshot };
    });
  }

  minimize() {
    this.isMinimized = true;
  }

  expand() {
    this.isMinimized = false;
  }

  openGuideList() {
    this.closeUserDataModal();
    this.closeTourDetails();
    this.isGuideListModalVisible = true;
  }

  closeGuideList() {
    this.isGuideListModalVisible = false;
  }

  closeTourDetails() {
    this.isTourDetailsModalVisible = false;
    this.isTourDetailsOpenedFromGuideList = false;
    this.selectedGuideId = undefined;
  }

  onGuideListButtonClicked() {
    if (this.isGuideListModalVisible) {
      this.closeGuideList();
      return;
    }

    this.closeAllModals();
    this.isGuideListModalVisible = true;
  }

  onTourDetailsButtonClicked() {
    if (this.isTourDetailsModalVisible) {
      this.closeTourDetails();
      return;
    }

    this.closeAllModals();
    this.isTourDetailsModalVisible = true;
    this.selectedGuideId = this.runningGuide?.id;
  }

  openTourDetails() {
    this.closeGuideList();
    this.closeUserDataModal();
    this.isTourDetailsModalVisible = true;
  }

  toggleReportBugModal() {
    if (this.isReportBugModalVisible) {
      this.closeReportBugModal();
      return;
    }

    this.closeAllModals();
    this.isReportBugModalVisible = true;
  }

  closeReportBugModal() {
    this.isReportBugModalVisible = false;
  }

  closeAllModals() {
    this.closeUserDataModal();
    this.closeGuideList();
    this.closeTourDetails();
    this.closeReportBugModal();
    this.closeStateDataModal();
    this.closeChecklistListModal();
  }

  closeUserDataModal() {
    this.isUserDataModalVisible = false;
  }

  toggleUserDataModal() {
    if (this.isUserDataModalVisible) {
      this.closeUserDataModal();
      return;
    }

    this.closeAllModals();
    this.isUserDataModalVisible = true;
  }

  toggleStateDataModal() {
    if (this.isStateDataModalVisible) {
      this.closeStateDataModal();
      return;
    }

    this.closeAllModals();
    this.isStateDataModalVisible = true;
  }

  closeStateDataModal() {
    this.isStateDataModalVisible = false;
  }

  toggleChecklistListModal() {
    if (this.isChecklistListModalVisible) {
      this.closeChecklistListModal();
      return;
    }

    this.closeAllModals();
    this.isChecklistListModalVisible = true;
  }

  closeChecklistListModal() {
    this.isChecklistListModalVisible = false;
  }

  selectGuide(guideId: string) {
    this.selectedGuideId = guideId;
    this.advancedDetailsTab = "guide";
    this.isTourDetailsOpenedFromGuideList = true;
    this.closeGuideList();
    this.openTourDetails();
  }

  setAdvancedDetailsEnabled(isEnabled: boolean) {
    this.isAdvancedDetailsEnabled = isEnabled;
  }

  setAdvancedDetailsTab(tab: AdvancedDetailsTab) {
    this.advancedDetailsTab = tab;
  }

  setTourDetailsModalWidth(width: number) {
    this.tourDetailsModalWidth = width;
  }

  enableSelectorPickerMode(guideId: string, stepId: string) {
    this.isSelectorPickerActive = true;
    this.selectorPickingState = { guideId, stepId };
  }

  disableSelectorPickerMode() {
    this.isSelectorPickerActive = false;
    this.selectorPickingState = null;
  }

  handleSelectorPicked({ selector, rank, url }: { selector: string; rank: number; url: string }) {
    if (!this.selectorPickingState?.guideId || !this.selectorPickingState?.stepId) return;

    const { guideId, stepId } = this.selectorPickingState!;

    runInAction(() => {
      this.hideSelectorUpdateSuccessToast();
      this.hideSelectorUpdateFailedToast();

      if (!this.editableGuides[guideId]) {
        const originalGuide = this.guideRegistry.findById(guideId);
        if (originalGuide) {
          const guideData = toJS(originalGuide, { recurseEverything: true });
          this.editableGuides[guideId] = new models.Guide(guideData);
        } else {
          console.error(`Original guide not found for guideId: ${guideId} when trying to create editable copy.`);
          return;
        }
      }

      const editableGuide = this.editableGuides[guideId];
      if (editableGuide?.tour_playback) {
        let stepFoundAndUpdated = false;
        for (const section of editableGuide.tour_playback.sections) {
          const stepToUpdate = section.steps.find((s: any) => s.id === stepId);
          if (stepToUpdate && stepToUpdate.attributes) {
            (stepToUpdate.attributes as any).targetElementSelector = selector;
            (stepToUpdate.attributes as any).targetElementSelectorRank = rank;
            if (stepToUpdate.attributes.onTrackConditions) {
              stepToUpdate.attributes.onTrackConditions.conditions.forEach((condition) => {
                if (condition.isManaged && condition.left_operand === "selector") {
                  condition.right_operand = selector;
                }
              });
            }
            stepFoundAndUpdated = true;
            break;
          }
        }
        if (!stepFoundAndUpdated) {
          console.warn(
            `Step ${stepId} not found or placement missing in editable guide's tour_playback for guide ${guideId}.`
          );
        }
      } else {
        console.warn(
          `[DebuggerManager] Editable guide or its tour_playback for guide ${guideId} not available for live update.`
        );
      }

      if (!this.pendingSelectorUpdatesByGuide[guideId]) {
        this.pendingSelectorUpdatesByGuide[guideId] = [];
      }
      const updatesForCurrentGuide = this.pendingSelectorUpdatesByGuide[guideId];
      const existingUpdateIndex = updatesForCurrentGuide.findIndex((update) => update.stepId === stepId);
      const newUpdate = { stepId, selector, url, rank };

      if (existingUpdateIndex > -1) {
        updatesForCurrentGuide[existingUpdateIndex] = newUpdate;
      } else {
        updatesForCurrentGuide.push(newUpdate);
      }
    });

    this.disableSelectorPickerMode();
  }

  submitSelectorUpdates() {
    if (!this.selectedGuideId) {
      console.warn("No guide selected to submit updates for.");
      return;
    }

    const updatesForSelectedGuide = this.pendingSelectorUpdatesByGuide[this.selectedGuideId];

    if (!updatesForSelectedGuide || updatesForSelectedGuide.length === 0) {
      console.warn("No pending selector updates to submit for the selected guide.");
      return;
    }

    this.hideSelectorUpdateSuccessToast();
    this.hideSelectorUpdateFailedToast();
    this.isSelectorUpdateLoading = true;
    this.lastSubmittedGuideIdForSelectorUpdates = this.selectedGuideId;

    const updatesWithScreenshots = updatesForSelectedGuide.map((update) => {
      const screenshotData = this.pendingScreenshotsByStep[update.stepId];
      if (screenshotData && screenshotData.guideId === this.selectedGuideId) {
        return { ...update, screenshot: screenshotData.screenshot };
      }
      return { ...update, screenshot: "" };
    });

    window.postMessage(
      {
        type: `pyne-debugger/save-selector`,
        guideId: this.selectedGuideId,
        updates: toJS(updatesWithScreenshots),
      },
      "*"
    );
  }

  clearState() {
    this.tourLifecycleManager.clearState();
    reloadWindow();
  }

  hideSelectorUpdateSuccessToast() {
    this.isSelectorUpdateSuccessToastVisible = false;
  }

  hideSelectorUpdateFailedToast() {
    this.isSelectorUpdateFailedToastVisible = false;
  }

  get isTourActive() {
    return !!this.tourPlaybackManager;
  }

  get selectedGuide() {
    if (!this.selectedGuideId) return null;
    return this.guideRegistry.findById(this.selectedGuideId);
  }

  get currentGuideTargetingDetails() {
    if (!this.currentGuide) return null;
    return this.getGuideEvaluationResult(this.currentGuide.id) ?? null;
  }

  get currentTourPlayback() {
    if (this.selectedGuideId && this.editableGuides[this.selectedGuideId]) {
      return this.editableGuides[this.selectedGuideId]?.tour_playback ?? null;
    }
    const selectedGuideFromRegistry = this.selectedGuideId ? this.guideRegistry.findById(this.selectedGuideId) : null;
    return selectedGuideFromRegistry?.tour_playback ?? this.tourPlaybackManager?.tourPlayback ?? null;
  }

  get runningFlow() {
    return this.tourPlaybackManager?.currentFlow ?? null;
  }

  get currentFlow() {
    if (this.selectedFlowKey) {
      return this.tourPlaybackManager?.flows[this.selectedFlowKey] ?? null;
    }

    return this.runningFlow;
  }

  get runningFlowKey() {
    return this.tourPlaybackManager?.currentFlow?.key ?? null;
  }

  get selectedFlowState() {
    if (!this.selectedFlowKey) return null;
    return this.getFlowState(this.selectedFlowKey);
  }

  getFlowState(flowKey: FlowKey) {
    if (!this.tourPlaybackManager) return null;

    const isFallbackFlowStateRequested = flowKey !== "tour";
    if (isFallbackFlowStateRequested) {
      return this.tourPlaybackManager.flowState;
    }

    const isRunningFallbackFlow = this.runningFlowKey !== "tour";
    if (isRunningFallbackFlow) {
      const flowStateStack = this.tourPlaybackManager.flowStateStack;
      return flowStateStack[flowStateStack.length - 1];
    }

    return this.tourPlaybackManager.flowState;
  }

  get isTourDetailsModalBackButtonVisible() {
    return this.isTourDetailsOpenedFromGuideList;
  }

  get currentGuideFormattedTargetingResult() {
    const status = this.currentGuideTargetingDetails?.details.message as string;
    return !!status && status !== "" ? toTitleCase(replaceAll(status, "_", " ")) : "Unknown";
  }

  get currentGuide() {
    if (this.selectedGuideId && this.editableGuides[this.selectedGuideId]) {
      return this.editableGuides[this.selectedGuideId];
    }
    if (this.selectedGuideId) {
      const guideFromRegistry = this.guideRegistry.findById(this.selectedGuideId);
      if (guideFromRegistry) return new models.Guide(guideFromRegistry);
    }
    return this.tourLifecycleManager.currentGuide;
  }

  get tourPlaybackManager() {
    return this.tourLifecycleManager.currentTour;
  }

  get runningGuide() {
    return this.tourLifecycleManager.currentGuide;
  }

  get currentStep() {
    return this.selectedStep ?? this.runningStep;
  }

  get currentTourStep() {
    return this.isRunningTourFlow ? this.runningStep : this.lastActiveTourStep;
  }

  get runningStep() {
    return this.tourPlaybackManager?.currentStep;
  }

  get runningSection() {
    return this.tourPlaybackManager?.currentSection;
  }

  get currentSection() {
    const isSelectedStepDifferentFromRunningStep =
      this.selectedStepId && this.runningStep && this.selectedStepId !== this.runningStep.id;
    if (isSelectedStepDifferentFromRunningStep) {
      return this.currentFlow?.sections.find((s) => s.steps.find((step) => step.id === this.selectedStepId)) ?? null;
    }
    return this.runningSection ?? null;
  }

  get activeFlowKey() {
    return this.tourPlaybackManager?.currentFlow?.key ?? null;
  }

  isGuideRunning(guideId: string) {
    if (!this.tourLifecycleManager.currentGuide) return false;
    return this.tourLifecycleManager.currentGuide.id === guideId;
  }

  get isCurrentGuideRunning() {
    if (!this.currentGuide || !this.runningGuide) return false;

    return this.isGuideRunning(this.currentGuide.id);
  }

  setSelectedFlowKey(flowKey: FlowKey | null) {
    this.selectedFlowKey = flowKey;
  }

  get selectedStep() {
    if (!this.selectedStepId) return null;

    const stepInGuide = this.findStepInCurrentGuide(this.selectedStepId);
    if (stepInGuide) return new models.Step(stepInGuide);

    const isCurrentGuideActive = this.currentGuide && this.isGuideRunning(this.currentGuide.id);
    if (isCurrentGuideActive) {
      const stepInFlow = this.findStepInRunningFlow(this.selectedStepId);
      if (stepInFlow) return new models.Step(stepInFlow);
    }

    return null;
  }

  get isRunningFlowExists() {
    return !!this.runningFlow;
  }

  get isRunningTourFlow() {
    return !!this.runningFlow?.key && this.runningFlow.key === "tour";
  }

  findStepInCurrentGuide(stepId: string) {
    return this.currentGuide?.tour_playback?.sections.flatMap((s) => s.steps).find((s) => s.id === stepId);
  }

  findStepInRunningFlow(stepId: string) {
    return this.runningFlow?.sections.flatMap((s) => s.steps).find((s) => s.id === stepId);
  }

  isStepOnTrack(stepId: string) {
    if (!this.tourPlaybackManager) return true;
    return this.tourPlaybackManager.tourTrackManager.isOnTrack(stepId);
  }

  isStepTargetElementFound(stepId: string) {
    if (!this.tourPlaybackManager) return true;
    const result = this.tourPlaybackManager.tourTrackManager.getDetailedResult(stepId);
    const managedCondition = result.find((c) => c.condition.isManaged);
    return !!managedCondition?.isSatisfied;
  }

  get currentStepOnTrackResult() {
    if (!this.currentStep) return undefined;
    return this.tourPlaybackManager?.tourTrackManager.getResult(this.currentStep.id);
  }

  get currentStepDetailedOnTrackResult() {
    if (!this.currentStep) return undefined;
    return this.tourPlaybackManager?.tourTrackManager.getDetailedResult(this.currentStep.id);
  }

  selectStep(stepId: string) {
    this.selectedStepId = stepId;
    if (stepId === this.runningStep?.id) {
      this.followRunningStep();
    } else {
      this.stopFollowActiveStep();
    }

    if (!this.isAdvancedDetailsEnabled) {
      this.isAdvancedDetailsEnabled = true;
    }

    this.setAdvancedDetailsTab("step");
  }

  clearSelectedStep() {
    this.selectedStepId = undefined;
  }

  stopFollowRunningStepFn?: () => void;

  followRunningStep() {
    this.stopFollowRunningStepFn = autorun(() => {
      this.selectedStepId = this.currentTourStep?.id;
    });
  }

  stopFollowActiveStep() {
    this.stopFollowRunningStepFn?.();
    this.stopFollowRunningStepFn = undefined;
  }

  get guides() {
    return sortBy(this.guideRegistry.guides, (guide) => new Date(guide.created_at).toISOString()).reverse();
  }

  getGuideEvaluationResult(guideId: string) {
    return this.targetingEvaluator.findGuideEvaluationResultById(guideId);
  }

  getGuideEvaluationStatus(guideId: string) {
    const result = this.getGuideEvaluationResult(guideId);
    if (!result) return "Unknown";

    return (result.details.message as string) ?? "Unknown";
  }

  getChecklistEvaluationResult(checklistId: string) {
    return this.targetingEvaluator.findChecklistEvaluationResultById(checklistId);
  }

  getChecklistEvaluationStatus(checklistId: string) {
    const result = this.getChecklistEvaluationResult(checklistId);
    if (!result) return "Unknown";
    return (result.details.message as string) ?? "Unknown";
  }

  get runningChecklist() {
    return this.tourLifecycleManager.currentChecklist;
  }

  get checklists() {
    return this.checklistRegistry.checklists;
  }

  startTour() {
    const guideToStart = this.currentGuide;

    if (!guideToStart || (this.selectedGuideId && guideToStart.id !== this.selectedGuideId)) {
      console.warn("Cannot start tour: Resolved guide is not available or does not match the selected guide context.");
      return;
    }

    this.tourLifecycleManager.terminateCurrentTourAndStartNewTour(
      guideToStart,
      "trigger_from_debugger",
      "trigger_from_debugger"
    );
  }

  startTourFromStep(stepId: string) {
    const guideToStart = this.currentGuide;

    if (!guideToStart || (this.selectedGuideId && guideToStart.id !== this.selectedGuideId)) {
      console.warn(
        "Cannot start tour from step: Resolved guide is not available or does not match the selected guide context."
      );
      return;
    }

    this.tourLifecycleManager.terminateCurrentTourAndStartNewTour(
      guideToStart,
      "trigger_from_debugger",
      "trigger_from_debugger",
      {
        tourStartStrategy: { type: "resume", stepId },
      }
    );
  }

  terminateCurrentTour() {
    this.tourLifecycleManager.terminateCurrentTour("trigger_from_debugger");
  }

  get currentUser() {
    return this.pyneClient.currentUser;
  }

  get isEnabled() {
    return this.storage.isEnabled;
  }

  enableDevMode() {
    this.isDevModeEnabled = true;
  }

  get tourStartParams() {
    const initialTourStateSnapshot = this.initialTourStateSnapshot;
    const lastCheckpoint = initialTourStateSnapshot?.checkpoints?.[initialTourStateSnapshot?.checkpoints.length - 1];
    return {
      activeStepId: initialTourStateSnapshot?.activeStepId ?? "-",
      lastCompletedStepId: initialTourStateSnapshot?.lastCompletedStepId ?? "-",
      lastCheckpoitStepId: lastCheckpoint?.stepId ?? "-",
      lastCheckpointUrl: lastCheckpoint?.url ?? "-",
      lastPageUrl: initialTourStateSnapshot?.lastPageUrl ?? "-",
      isSuspended: initialTourStateSnapshot?.isSuspended ?? "-",
      tabId: initialTourStateSnapshot?.tabId ?? "-",
    };
  }

  get initialTourStateSnapshot() {
    return this.tourLifecycleManager.tourStartManager.initialTourStateSnapshot;
  }

  get tourPlaybackFlowContext() {
    const flowState = this.tourPlaybackManager?.flowState;
    return {
      currentTime: flowState?.currentTime,
      currentSectionIndex: flowState?.currentSectionIndex,
      currentStepIndex: flowState?.currentStepIndex,
    };
  }

  get tourPlaybackFlowStatus() {
    return this.tourPlaybackManager?.flowStatus;
  }

  get avatarState() {
    return this.tourPlaybackManager?.avatarManager.state?.value;
  }

  get avatarManager() {
    return this.tourPlaybackManager?.avatarManager;
  }

  get tourStartStrategy() {
    return this.tourLifecycleManager.lastTourStartStrategy;
  }

  get userState() {
    return {
      finishedTourGuideIds: this.userStateStorage.finishedTourGuideIds,
      dismissedTourGuideIds: this.userStateStorage.dismissedTourGuideIds,
      dismissedChecklistIds: this.userStateStorage.dismissedChecklistIds,
    };
  }

  isCheckpointCaptured(stepId: string) {
    return !!this.tourPlaybackManager?.checkpoints.find((checkpoint) => checkpoint.stepId === stepId);
  }

  get pendingUpdatesForSelectedGuide(): Array<{ stepId: string; selector: string; url: string; rank?: number }> {
    if (!this.selectedGuideId) return [];
    return this.pendingSelectorUpdatesByGuide[this.selectedGuideId] || [];
  }
}
